import * as THREE from "three";
import { TestLevelBridge } from "../debug/test_level_bridge.js";
import { getDisplayDebugInfo, isMobileDevice } from "../utils/device_utils.js";

export interface FrameFit
{
  frame_fill: number;
  min_depth: number;
  keep_above?: { point: THREE.Vector3; y: number };
  /** Highest NDC y any point may reach: the screen above it belongs to the HUD. */
  max_y?: number;
  max_zoom?: { size: number; screen_fraction: number; at: readonly THREE.Vector3[] };
  min_zoom?: { size: number; screen_fraction: number; overflow_fill: number };
}

export class CameraController
{
  private static readonly FIT_MAX_GROWTHS = 16;
  private static readonly FIT_BISECTIONS = 24;

  private static readonly FOV = 30;

  private static readonly COMPOSED_FOV = 45;
  private static readonly COMPOSED_EYE = new THREE.Vector3( 0, 3.7, 13 );
  private static readonly COMPOSED_TARGET = new THREE.Vector3( 0, 3.1, 0 );
  private static readonly COMPOSED_VIEW = CameraController.viewMatrix( CameraController.COMPOSED_EYE, CameraController.COMPOSED_TARGET );
  private static readonly LENS_ZOOM =
    Math.tan( THREE.MathUtils.degToRad( CameraController.COMPOSED_FOV * 0.5 ) )
    / Math.tan( THREE.MathUtils.degToRad( CameraController.FOV * 0.5 ) );

  private camera: THREE.PerspectiveCamera;
  private readonly look_target = new THREE.Vector3( 0, 2.75, 0 );
  private readonly home_eye = new THREE.Vector3();
  private readonly fit_anchor = new THREE.Vector3();
  private readonly fit_direction = new THREE.Vector3();
  private readonly fit_point = new THREE.Vector3();

  constructor()
  {
    // Headless batches (stability / simulation) use a fixed portrait viewport:
    // a hidden tab can report 0×0 (aspect NaN poisons the whole physics scene),
    // and different window sizes would change camera-driven table geometry and
    // make batch results non-reproducible across machines.
    // Use the exact mobile/Telegram-aware box already resolved by Game.
    const reference = document.getElementById( "menuCanvas" ) as HTMLCanvasElement | null;
    const reference_width = Number.parseFloat( reference?.style.width ?? "" );
    const reference_height = Number.parseFloat( reference?.style.height ?? "" );
    let viewport_width = Number.isFinite( reference_width ) ? Math.round( reference_width ) : window.innerWidth;
    let viewport_height = Number.isFinite( reference_height ) ? Math.round( reference_height ) : window.innerHeight;

    if ( TestLevelBridge.isBatchActive() || !( viewport_width > 50 ) || !( viewport_height > 50 ) )
    {
      viewport_width = 390;
      viewport_height = 844;
    }
    const game_width = viewport_width;

    this.camera = new THREE.PerspectiveCamera(
      CameraController.FOV,
      game_width / viewport_height,
      0.1,
      1000
    );

    this.camera.position.set(
      0,
      4,
      13
    );

    this.camera.lookAt( this.look_target );
    this.camera.updateMatrixWorld( true );
    this.home_eye.copy( this.camera.position );

    const debug = {
      ...getDisplayDebugInfo(),
      selectedMode: isMobileDevice() ? "mobile-fullscreen" : "desktop-3:4",
      cameraGameWidth: game_width,
      cameraAspect: this.camera.aspect,
      cameraFov: this.camera.fov,
    };
    ( window as any ).__displayDebug = {
      ...( ( window as any ).__displayDebug ?? {} ),
      camera: debug,
    };
    console.info( "[DisplayDebug][Camera]", debug );
  }

  public getCamera(): THREE.PerspectiveCamera
  {
    return this.camera;
  }

  public getHomeEye(): THREE.Vector3
  {
    return this.home_eye.clone();
  }

  public fromComposedView( point: THREE.Vector3 ): THREE.Vector3
  {
    return this.toLensSpace( point ).applyMatrix4( this.camera.matrixWorld );
  }

  public composedDepth( point: THREE.Vector3 ): number
  {
    return -this.toLensSpace( point ).z;
  }

  public floorPointAt( screen_y: number, floor_y: number ): THREE.Vector3 | null
  {
    const eye = this.camera.position;
    const ray = new THREE.Vector3( 0, 1 - 2 * screen_y, 0.5 ).unproject( this.camera ).sub( eye );

    if ( ray.y >= -1e-6 || eye.y <= floor_y )
      return null;

    return eye.clone().addScaledVector( ray, ( floor_y - eye.y ) / ray.y );
  }

  public fitAlong( anchor: THREE.Vector3, points: readonly THREE.Vector3[], fit: FrameFit ): void
  {
    const home_distance = this.home_eye.distanceTo( anchor );

    this.fit_anchor.copy( anchor );
    this.fit_direction.copy( this.home_eye ).sub( anchor ).normalize();

    if ( points.length === 0 )
    {
      this.placeAt( home_distance );
      return;
    }

    let overflows = 0;
    let fits = home_distance;

    for ( let i = 0; !this.fitsAt( fits, points, fit ); i++ )
    {
      if ( i === CameraController.FIT_MAX_GROWTHS )
      {
        this.placeAt( home_distance );
        return;
      }

      overflows = fits;
      fits *= 2;
    }

    for ( let i = 0; i < CameraController.FIT_BISECTIONS; i++ )
    {
      const mid = ( overflows + fits ) * 0.5;

      if ( this.fitsAt( mid, points, fit ) )
        fits = mid;
      else
        overflows = mid;
    }

    this.placeAt( fits );
  }

  private static viewMatrix( eye: THREE.Vector3, target: THREE.Vector3 ): THREE.Matrix4
  {
    const camera = new THREE.PerspectiveCamera();
    camera.position.copy( eye );
    camera.lookAt( target );
    camera.updateMatrixWorld( true );

    return camera.matrixWorldInverse.clone();
  }

  private toLensSpace( point: THREE.Vector3 ): THREE.Vector3
  {
    const view = point.clone().applyMatrix4( CameraController.COMPOSED_VIEW );
    view.z *= CameraController.LENS_ZOOM;

    return view;
  }

  private fitsAt( distance: number, points: readonly THREE.Vector3[], fit: FrameFit ): boolean
  {
    this.placeAt( distance );

    if ( fit.keep_above && this.fromComposedView( fit.keep_above.point ).y < fit.keep_above.y )
      return false;

    if ( fit.max_zoom )
    {
      const spots = fit.max_zoom.at.length > 0 ? fit.max_zoom.at : [ this.fit_anchor ];

      for ( const spot of spots )
        if ( this.screenHeight( spot, fit.max_zoom.size ) > fit.max_zoom.screen_fraction )
          return false;
    }

    const nearest = Math.max( fit.min_depth, this.camera.near );
    let overflow = 0;
    let top = -Infinity;

    for ( const point of points )
    {
      const p = this.fit_point.copy( point ).applyMatrix4( this.camera.matrixWorldInverse );

      if ( -p.z < nearest )
        return false;

      p.applyMatrix4( this.camera.projectionMatrix );
      overflow = Math.max( overflow, Math.abs( p.x ), Math.abs( p.y ) );
      top = Math.max( top, p.y );
    }

    if ( fit.max_y !== undefined && top > fit.max_y )
      return false;

    if ( overflow <= fit.frame_fill )
      return true;

    return !!fit.min_zoom
      && overflow <= fit.min_zoom.overflow_fill
      && this.screenHeight( this.fit_anchor, fit.min_zoom.size ) <= fit.min_zoom.screen_fraction;
  }

  private screenHeight( foot: THREE.Vector3, size: number ): number
  {
    const bottom = this.fit_point.copy( foot ).project( this.camera ).y;
    const top = this.fit_point.copy( foot ).setY( foot.y + size ).project( this.camera ).y;

    return ( top - bottom ) * 0.5;
  }

  private placeAt( distance: number ): void
  {
    this.camera.position.copy( this.fit_anchor ).addScaledVector( this.fit_direction, distance );
    this.camera.updateMatrixWorld( true );
  }
}